import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

import type { RootState } from '../index.js';

// Define the Phase interface
export interface Phase {
  id: string | number; // Changed from ID
  name: string;      // Changed from Name
}

// Define the Job interface
export interface Job {
  // Fields from DB 'projects' table
  id: number; // Changed from ID
  quote_no: string; // Changed from QuoteNo
  quote_name: string; // Changed from QuoteName
  full_combine_name: string; // Changed from FullCombineName
  accepted_date: string; // Changed from AcceptedDate
  phase_list: Phase[]; // Changed from PhaseList, holds parsed objects

  // Optional fields from backend Project schema for completeness
  quote_id?: number;
  customer_id?: number;
  cust_job_no?: string;
  loc_name?: string;
  job_status?: string;

  [key: string]: any; // Allow for dynamic property access
}

// Define the state type
interface JobsState {
  items: Job[];
  loading: boolean;
  error: string | null;
  filteredIds: (string | number)[] | null; // For search results
  searchMatches: Record<string | number, number>; // Map of ID to score
  searchTerm: string; // Store the current search term
}

// Initial state
const initialState: JobsState = {
  items: [],
  loading: true,
  error: null,
  filteredIds: null,
  searchMatches: {},
  searchTerm: ''
};

// Async thunk for fetching jobs
export const fetchJobs = createAsyncThunk<Job[], void, { rejectValue: string }>(
  'jobs/fetchJobs',
  async (_, { rejectWithValue }) => {
    try {
      const apiResponse = await fetch('/api/projects'); // Fetch from the new API endpoint
      if (!apiResponse.ok) {
        const errorData = await apiResponse.text();
        throw new Error(`Failed to fetch jobs: ${apiResponse.status} ${apiResponse.statusText} - ${errorData}`);
      }
      const projectsFromApi = await apiResponse.json() as any[]; // Raw projects from API

      // Transform to match client Job interface, especially parsing phase_list
      const transformedJobs: Job[] = projectsFromApi.map((project: any) => {
        let parsedPhaseList: Phase[] = [];
        if (project.phase_list && typeof project.phase_list === 'string') {
          try {
            // Assuming phase_list JSON contains objects matching the client Phase interface
            // e.g., [{ id: 1, name: 'Some Phase' }, ...]
            parsedPhaseList = JSON.parse(project.phase_list);
          } catch (e) {
            console.error(`Failed to parse phase_list for project id ${project.id}:`, e);
            // Keep phase_list as empty or handle error appropriately
          }
        } else if (Array.isArray(project.phase_list)) {
          // If it's already an array (e.g., if server pre-parses it)
          parsedPhaseList = project.phase_list;
        }

        // Map backend fields to frontend Job interface
        // console.log('[jobs.ts] fetchJobs - raw project from API:', JSON.stringify(project, null, 2));
        const transformedJob = {
          id: project.id,
          quote_no: project.quote_no,
          quote_name: project.quote_name,
          full_combine_name: project.full_combine_name,
          accepted_date: project.accepted_date,
          phase_list: parsedPhaseList,
          quote_id: project.quote_id,
          customer_id: project.customer_id,
          cust_job_no: project.cust_job_no,
          loc_name: project.loc_name,
          job_status: project.job_status,
        };
        // console.log('[jobs.ts] fetchJobs - transformed job for Redux:', JSON.stringify(transformedJob, null, 2));
        return transformedJob;
      });
      return transformedJobs;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to load job data');
    }
  }
);

/**
 * Creates a searchable text representation of a job that combines
 * all the searchable fields with appropriate weighting
 */
const createSearchableText = (job: Job): string => {
  // Get phase names from the job
  const phaseNames = job.phase_list.map(phase => phase.name).join(' '); // Changed PhaseList to phase_list, phase.Name to phase.name
  
  // Combine all searchable fields into one text
  // Repeat important fields to give them more weight in the search
  return [
    job.quote_no, // Changed
    job.quote_name, job.quote_name, // Changed & Duplicated for more weight
    job.full_combine_name, job.full_combine_name, // Changed & Duplicated for more weight
    phaseNames
  ].filter(Boolean).join(' ');
};

/**
 * Performs a search across all jobs and returns matching IDs
 */
const performSearch = (
  jobs: Job[], 
  searchTerm: string
): { matchingIds: (string | number)[], matches: Record<string | number, number> } => {
  // Prepare variables
  const matchingIds: (string | number)[] = [];
  const matches: Record<string | number, number> = {};
  const searchWords = searchTerm.toLowerCase().split(/\s+/).filter(word => word.length >= 2);
  
  if (searchWords.length === 0) {
    return { matchingIds: [], matches: {} };
  }
  
  // For each job, check if it matches the search criteria
  jobs.forEach(job => {
    const text = createSearchableText(job).toLowerCase();
    
    if (searchWords.length > 1) {
      // For multi-word search, require all words to be present
      let allWordsFound = true;
      const exactPhraseMatch = text.includes(searchTerm.toLowerCase());
      let matchCount = 0;
      
      // Check if ALL search words are in this job
      for (const word of searchWords) {
        if (word.length < 2) {continue;}
        
        // Create case-insensitive regex for the word
        const escapedWord = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const wordRegex = new RegExp(escapedWord, 'i');
        
        if (wordRegex.test(text)) {
          matchCount++;
        } else {
          allWordsFound = false;
          break;
        }
      }
      
      // Only include if all words were found
      if (allWordsFound) {
        matchingIds.push(job.id); // Changed job.ID to job.id
        // Score based on exact phrase match and match count
        const percentageMatched = matchCount / searchWords.length;
        const baseScore = exactPhraseMatch ? 95 : 85;
        matches[job.id] = Math.round(baseScore * percentageMatched); // Changed job.ID to job.id
      }
    } else {
      // Single word search - include if matched
      if (text.includes(searchTerm.toLowerCase())) {
        matchingIds.push(job.id); // Changed job.ID to job.id
        matches[job.id] = 85; // Changed job.ID to job.id
      }
    }
  });
  
  return { matchingIds, matches };
};

/**
 * Simple match segment finder for highlighting
 */
export const findMatchSegment = (
  text: string, 
  searchTerm: string
): [number, number, number] => {
  if (!text || !searchTerm.trim()) {return [-1, -1, 0];}
  
  const lowerText = text.toLowerCase();
  const searchWords = searchTerm.toLowerCase().trim().split(/\s+/);
  
  // Try exact match first
  const exactIndex = lowerText.indexOf(searchTerm.toLowerCase());
  if (exactIndex >= 0) {
    return [exactIndex, exactIndex + searchTerm.length, 100];
  }
  
  // Try to match individual words
  for (const word of searchWords) {
    if (word.length < 3) {continue;}
    
    const wordIndex = lowerText.indexOf(word);
    if (wordIndex >= 0) {
      return [wordIndex, wordIndex + word.length, 85];
    }
    
    // Try prefix matching for longer words
    if (word.length >= 4) {
      const prefix = word.substring(0, Math.ceil(word.length * 0.6));
      const prefixIndex = lowerText.indexOf(prefix);
      
      if (prefixIndex >= 0) {
        // Find word boundary
        let end = prefixIndex + prefix.length;
        while (end < lowerText.length && /[a-z0-9]/.test(lowerText[end])) {
          end++;
        }
        return [prefixIndex, end, 75];
      }
    }
  }
  
  return [-1, -1, 0];
};

// Create the slice
const jobsSlice = createSlice({
  name: 'jobs',
  initialState,
  reducers: {
    // Action to search jobs with improved multi-word handling
    searchJobs: (state, action: PayloadAction<string>) => {
      const searchTerm = action.payload.trim();
      state.searchTerm = searchTerm;
      
      if (!searchTerm) {
        state.filteredIds = null;
        state.searchMatches = {};
        return;
      }
      
      // Use our custom search function
      const { matchingIds, matches } = performSearch(state.items, searchTerm);
      
      if (matchingIds.length > 0) {
        // Sort matching IDs by exact phrase match first, then alphabetically by job name
        const sortedMatches = [...matchingIds].sort((a, b) => {
          const jobA = state.items.find(j => j.id === a); // Changed j.ID to j.id
          const jobB = state.items.find(j => j.id === b); // Changed j.ID to j.id
          
          if (!jobA || !jobB) {return 0;}
          
          const textA = createSearchableText(jobA).toLowerCase();
          const textB = createSearchableText(jobB).toLowerCase();
          
          // Check for exact phrase match
          const aHasExact = textA.includes(searchTerm.toLowerCase());
          const bHasExact = textB.includes(searchTerm.toLowerCase());
          
          // Sort by exact match first
          if (aHasExact && !bHasExact) {return -1;}
          if (!aHasExact && bHasExact) {return 1;}
          
          // Then by quote name
          return jobA.quote_name.localeCompare(jobB.quote_name); // Changed QuoteName to quote_name
        });
        
        state.filteredIds = sortedMatches;
        state.searchMatches = matches;
      } else {
        state.filteredIds = [];
        state.searchMatches = {};
      }
    },
    
    // Action to reset search
    resetSearch: (state) => {
      state.filteredIds = null;
      state.searchMatches = {};
      state.searchTerm = '';
    },
    
    // Action to filter by phase and date range
    filterJobs: (state, action: PayloadAction<{
      phase?: string;
      dateRange?: { from?: Date; to?: Date; };
    }>) => {
      const { phase, dateRange } = action.payload;
      
      // If no filters and no search, don't filter
      if (
        (!phase || phase === 'All') &&
        !dateRange?.from &&
        !dateRange?.to &&
        !state.filteredIds
      ) {
        return;
      }
      
      // Start with all jobs or current filtered set
      let filteredJobs = state.items;
      
      // Apply existing search filter if any
      if (state.filteredIds) {
        const idSet = new Set(state.filteredIds);
        filteredJobs = filteredJobs.filter(job => idSet.has(job.id)); // Changed job.ID to job.id
      }
      
      // Apply dropdown filters
      if (phase && phase !== "All") {
        filteredJobs = filteredJobs.filter(job =>
          job.phase_list.some(p => p.name === phase) // Changed PhaseList to phase_list, p.Name to p.name
        );
      }
      
      // Apply date range filter
      if (dateRange?.from) {
        const fromDate = new Date(dateRange.from);
        fromDate.setHours(0, 0, 0, 0);
        filteredJobs = filteredJobs.filter(job => {
          const jobDate = new Date(job.accepted_date); // Changed AcceptedDate to accepted_date
          return jobDate >= fromDate;
        });
      }
      if (dateRange?.to) {
        const toDate = new Date(dateRange.to);
        toDate.setHours(23, 59, 59, 999);
        filteredJobs = filteredJobs.filter(job => {
          const jobDate = new Date(job.accepted_date); // Changed AcceptedDate to accepted_date
          return jobDate <= toDate;
        });
      }
      
      // Update filteredIds
      state.filteredIds = filteredJobs.map(job => job.id); // Changed job.ID to job.id
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchJobs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJobs.fulfilled, (state, action: PayloadAction<Job[]>) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchJobs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'An error occurred';
      });
  },
});

// Export actions
export const { searchJobs, resetSearch, filterJobs } = jobsSlice.actions;

// Export selectors 
export const selectAllJobs = (state: RootState) => state.jobs?.items || [];
export const selectJobsLoading = (state: RootState) => state.jobs?.loading || false;
export const selectJobsError = (state: RootState) => state.jobs?.error || null;

// Selector for filtered jobs
export const selectFilteredJobs = (state: RootState) => {
  if (!state.jobs) {return [];}
  
  const { items, filteredIds } = state.jobs;
  
  if (!filteredIds) {
    return items; // No filter, return all jobs
  }
  
  // Filter jobs by IDs
  const idSet = new Set(filteredIds);
  return items.filter((job: Job) => idSet.has(job.id)); // Changed job.ID to job.id
};

// Selector for search matches
export const selectSearchMatches = (state: RootState) => 
  state.jobs?.searchMatches || {};

// Selector for current search term
export const selectSearchTerm = (state: RootState) => 
  state.jobs?.searchTerm || "";

// Export reducer
export default jobsSlice.reducer;
